import { isStripeCheckoutStore } from '@/lib/pay/hosted-checkout-store';
import {
    formatConfirmOrderFailDetail,
    mapConfirmStatusToPayPalFailReason,
    reportPayPalFail,
    reportPayPalSuccess,
    type TPayPalArmsFailReason,
} from '@/utils/arms/paypal-events';
import {
    mapConfirmStatusToStripeFailReason,
    reportStripeFail,
    reportStripeSuccess,
    type TStripeArmsFailReason,
} from '@/utils/arms/stripe-events';

import type { readPersistedPayPendingMeta } from '@/hooks/use-pay/paypal-checkout-redirect';
import type { IRechargePayExtras, IRechargeResultStaticContext } from '@/utils/tracker/pay';

type TPayPendingMeta = NonNullable<ReturnType<typeof readPersistedPayPendingMeta>>;

function metaToStaticContext(meta: TPayPendingMeta): IRechargeResultStaticContext {
    return {
        trigger_scene: meta.trigger_scene,
        gear_id: meta.gear_id,
        is_balance_enough: meta.is_balance_enough,
        paywall_template_id: meta.paywall_template_id,
        version: meta.version ?? 0,
        checksum: meta.checksum ?? '',
        book_id: meta.book_id,
        chapter_id: meta.chapter_id,
        chapter_price: meta.chapter_price,
        is_auto_unlock_on: meta.is_auto_unlock_on,
    };
}

function metaToPayExtras(meta: TPayPendingMeta): IRechargePayExtras {
    return {
        money: meta.money,
        goods_type: meta.goods_type,
        is_renew: meta.is_renew,
        coin: meta.coin,
        bonus: meta.bonus,
    };
}

function isStripeReturn(meta: TPayPendingMeta | null): boolean {
    return isStripeCheckoutStore(meta?.store);
}

function buildArmsPropsFromMeta(meta: TPayPendingMeta | null, returnScene: string) {
    const ctx = meta ? metaToStaticContext(meta) : null;
    const extras = meta ? metaToPayExtras(meta) : null;
    return {
        return_scene: returnScene,
        goods_type: extras?.goods_type ?? ('recharge' as const),
        gear_id: ctx?.gear_id ?? '',
        trigger_scene: ctx?.trigger_scene ?? '',
        paywall_template_id: ctx?.paywall_template_id,
        book_id: ctx?.book_id,
        chapter_id: ctx?.chapter_id,
        money: extras?.money,
        payment_method: meta?.payment_method ?? '',
    };
}

export function reportCheckoutReturnSuccess(orderId: string, meta: TPayPendingMeta | null, returnScene: string): void {
    const armsProps = buildArmsPropsFromMeta(meta, returnScene);
    if (isStripeReturn(meta)) {
        reportStripeSuccess({ order_id: orderId, ...armsProps });
        return;
    }
    reportPayPalSuccess({ order_id: orderId, ...armsProps });
}

export function reportCheckoutReturnFail(
    orderId: string,
    meta: TPayPendingMeta | null,
    returnScene: string,
    failReason: TPayPalArmsFailReason | TStripeArmsFailReason,
    failDetail?: string
): void {
    const armsProps = buildArmsPropsFromMeta(meta, returnScene);
    if (isStripeReturn(meta)) {
        const stripeFailReason: TStripeArmsFailReason =
            failReason === 'paypal_return_cancel' ? 'stripe_return_cancel' : (failReason as TStripeArmsFailReason);
        reportStripeFail({
            ...armsProps,
            order_id: orderId || undefined,
            fail_reason: stripeFailReason,
            fail_detail: failDetail,
        });
        return;
    }
    reportPayPalFail({
        ...armsProps,
        order_id: orderId || undefined,
        fail_reason: failReason as TPayPalArmsFailReason,
        fail_detail: failDetail,
    });
}

export function mapConfirmStatusToCheckoutFailReason(
    status: string,
    meta: TPayPendingMeta | null
): TPayPalArmsFailReason | TStripeArmsFailReason {
    return isStripeReturn(meta)
        ? mapConfirmStatusToStripeFailReason(status)
        : mapConfirmStatusToPayPalFailReason(status);
}

export { formatConfirmOrderFailDetail, metaToPayExtras, metaToStaticContext };
